import { Button, Center, Field, Input, Stack, Switch, Text } from '@chakra-ui/react';
import { Dialog } from '@halo/ui-react';
import type { User } from '@halo/chat-sdk-react';
import { Avatar } from '@halo/ui-chat-react';
import BackUpIcon from '@/assets/images/contacts/svg/backUp.svg?react';
import DeleteIcon from '@/assets/images/contacts/svg/delete.svg?react';
import { useContactsQuery } from '@/scripts/hooks';
import { InfoItem } from './InfoItem';
import { ContactDeleteDialog } from './ContactDeleteDialog';
import EditContract from './EditContract';

export interface SetMyContactDialogForm {
  remarkFirstName: string;
  remarkLastName: string;
}

export interface SetMyContactDialogProps {
  loading: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onOK: (form: SetMyContactDialogForm) => Promise<void>;
  onClose?: () => void;
  onDeleteContact: () => void;
  user: User | null;
  myContactSettingOnlyEdit?: boolean;
  syncBackupContactChecked: boolean;
}

const MyContactSettingDialog = ({
  loading,
  isOpen,
  setIsOpen,
  onOK,
  onDeleteContact,
  user,
  myContactSettingOnlyEdit = false,
  syncBackupContactChecked,
}: SetMyContactDialogProps) => {
  const { t } = useTranslation();
  const [remarkFirstName, setRemarkFirstName] = useState('');
  const [remarkLastName, setRemarkLastName] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { isContact } = useContactsQuery(user?.user_id ?? '');

  useEffect(() => {
    setRemarkFirstName(user?.remark_first_name || '');
    setRemarkLastName(user?.remark_last_name || '');
  }, [user]);

  return (
    <>
      <Dialog.Root
        placement="center"
        open={isOpen && !myContactSettingOnlyEdit}
        onOpenChange={(details) => setIsOpen(details.open)}
        closeOnInteractOutside={false}
        unmountOnExit
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="xl" w="378px" maxW="100%" bg="bg.subtle">
            <Dialog.Header
              p="6"
              pb="0"
              fontSize="md"
              fontWeight="semibold"
              justifyContent="space-between"
            >
              <Text>{myContactSettingOnlyEdit ? t('action.edit') : t('action.addContact')}</Text>
              {/* <Center cursor="pointer" onClick={onClose}>
                <CloseIcon />
              </Center> */}
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap="2.5">
                <Center p="4" pb="2">
                  <Avatar src={user?.avatar} size="2xl" activeShake={false} />
                </Center>
                <Field.Root>
                  <Input
                    variant="subtle"
                    bg="bg"
                    size="sm"
                    fontWeight="semibold"
                    maxLength={32}
                    borderRadius="lg"
                    borderColor="border.input"
                    placeholder={
                      user?.first_name
                        ? t('chat.hasFirstNamePlaceholder', { name: user?.first_name })
                        : t('chat.noFirstNamePlaceholder')
                    }
                    value={remarkFirstName}
                    onChange={(e) => setRemarkFirstName(e.target.value)}
                  />
                </Field.Root>
                <Field.Root>
                  <Input
                    variant="subtle"
                    bg="bg"
                    size="sm"
                    fontWeight="semibold"
                    maxLength={32}
                    borderRadius="lg"
                    borderColor="border.input"
                    placeholder={`Last Name${user?.last_name ? ` (${user?.last_name})` : ''}`}
                    value={remarkLastName}
                    onChange={(e) => setRemarkLastName(e.target.value)}
                  />
                </Field.Root>
                {(!myContactSettingOnlyEdit || isContact) && (
                  <InfoItem
                    icon={<BackUpIcon />}
                    label={t('chat.backupContacts')}
                    extra={
                      <Switch.Root checked={syncBackupContactChecked} disabled>
                        <Switch.HiddenInput />
                        <Switch.Control />
                      </Switch.Root>
                    }
                  ></InfoItem>
                )}
                {isContact && (
                  <InfoItem
                    icon={<DeleteIcon />}
                    label={t('chat.deleteContact')}
                    onClick={() => setOpenDeleteDialog(true)}
                  ></InfoItem>
                )}
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                h="10"
                borderRadius="lg"
                variant="ghost"
                color="fg"
                onClick={() => setIsOpen(false)}
              >
                {t('buttons.cancel')}
              </Button>
              <Button
                minW="96px"
                h="10"
                borderRadius="lg"
                loading={loading}
                onClick={() =>
                  onOK({
                    remarkFirstName: remarkFirstName.trim(),
                    remarkLastName: remarkLastName.trim(),
                  })
                }
              >
                {t('buttons.add')}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
      <EditContract
        loading={loading}
        isOpen={isOpen && myContactSettingOnlyEdit}
        setIsOpen={setIsOpen}
        onOK={onOK}
        myContactSettingOnlyEdit={myContactSettingOnlyEdit}
        user={user}
        syncBackupContactChecked={syncBackupContactChecked}
        onDelOpen={() => setOpenDeleteDialog(true)}
      />
      <ContactDeleteDialog
        loading={loading}
        user={user}
        open={openDeleteDialog}
        onOK={onDeleteContact}
        onClose={() => setOpenDeleteDialog(false)}
      />
    </>
  );
};

const MemoizedMyContactSettingDialog = memo(MyContactSettingDialog);

export { MemoizedMyContactSettingDialog as MyContactSettingDialog };
